import type { FC } from 'react';

import styled, { css } from 'styled-components';

import { HybrisCmsLink } from '../../core/link/HybrisCmsLink';
import type { NavigationItem } from '~/utilities/graphql/codegen';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const S = {
  NavLink: styled(HybrisCmsLink)<{ $active: boolean }>`
    ${({ theme }) => css`
      font-size: ${theme.font.size[3]};
      font-weight: ${theme.variables.font.weight.medium};
      line-height: var(--top-nav-height);
      margin: 0;
      padding: ${theme.padding.none} ${theme.padding['2xs']};
      text-transform: uppercase;
      display: inline-block;
      text-decoration: none;

      &:hover,
      &:focus-visible {
        span {
          border-color: ${theme.colors.content.component.header.color};
          ${getSiteStyles(theme.siteType, {
            [SiteType.Outlet]: css`
              border-color: ${theme.colors.background.component.button.primary.hover};
              color: ${theme.colors.background.component.button.primary.hover};
            `,
          })}
        }
      }
    `}
  `,

  LinkLabel: styled.span<{ $active: boolean }>`
    ${({ theme, $active }) => css`
      border-bottom: 2px solid
        ${$active ? theme.colors.content.component.header.color : 'transparent'};
      color: ${theme.colors.content.component.header.color};

      ${getSiteStyles(
        theme.siteType,
        $active
          ? {
              [SiteType.Outlet]: css`
                border-color: ${theme.colors.background.component.button.primary.hover};
              `,
            }
          : {}
      )}
    `}
  `,
};

interface Props {
  link?: NavigationItem | null;
  superCategory: string;
}

export const NavLink: FC<Props> = ({ link, superCategory }) => {
  const { label, url, target } = link ?? {};

  if (!label || !url) {
    return null;
  }

  const isActiveLink = superCategory?.toLowerCase() === label.toLowerCase();

  return (
    <S.NavLink href={url} $active={isActiveLink} target={target}>
      <S.LinkLabel
        $active={isActiveLink}
        data-testid={`top-navigation-link${isActiveLink ? '-active' : ''}`}
      >
        {label}
      </S.LinkLabel>
    </S.NavLink>
  );
};
